import { Close, VisibilityOutlined } from '@mui/icons-material';
import { Badge, Box, ButtonBase, IconButton, Tooltip, Typography } from '@mui/material';

type ContextProps = { expanded: boolean; contentId: string; attributes: Record<string, string> };

export function PromptChatContextButton({ expanded, contentId, onToggle, attributes }: ContextProps & { onToggle: () => void }) {
	const count = Object.keys(attributes).length;
	return <Tooltip title={`Context (${count})`}>
		<ButtonBase id={`${contentId}-toggle`} aria-label={`Context: ${count} attributes`} aria-expanded={expanded} aria-controls={contentId} onClick={onToggle}
			sx={{ width: 28, height: 28, flexShrink: 0, borderRadius: '50%', fontSize: '0.7rem', fontWeight: 600,
				bgcolor: expanded ? 'primary.main' : 'action.selected', color: expanded ? 'primary.contrastText' : 'text.secondary',
				'&:hover': { opacity: 0.8 }, '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 } }}>
			<Badge badgeContent={count} showZero sx={{ '& .MuiBadge-badge': {
				minWidth: 14, height: 14, px: 0.25, fontSize: '0.55rem', fontWeight: 600,
				bgcolor: 'background.paper', color: 'text.primary', border: '1px solid', borderColor: 'divider',
			} }}>
				<VisibilityOutlined sx={{ fontSize: 18 }} />
			</Badge>
		</ButtonBase>
	</Tooltip>;
}

/** Read-only preview of the context sent by this chat; Mode and attachments have separate controls. */
export default function PromptChatContextIndicator({ expanded, contentId, onClose, attributes, emptyText = 'No context selected.' }: ContextProps & { onClose: () => void; emptyText?: string }) {
	const entries = Object.entries(attributes);
	const target = attributes.ScreenSelectionName || attributes.ScreenName || attributes.ScreenArea;
	return <Box id={contentId} role="region" aria-label="Context attributes" hidden={!expanded}
		sx={{ mb: expanded ? 1 : 0, p: 1, bgcolor: 'action.hover', borderRadius: 1, color: 'text.secondary', minWidth: 0 }}>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
			<Typography variant="caption" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>Context ({entries.length}){target ? ` · ${target}` : ''}</Typography>
			<Tooltip title="Close context"><IconButton size="small" aria-label="Close context" onClick={() => {
				onClose();
				document.getElementById(`${contentId}-toggle`)?.focus();
			}} sx={{ flexShrink: 0, p: 0.25, color: 'text.secondary' }}><Close sx={{ fontSize: 16 }} /></IconButton></Tooltip>
		</Box>
		<Typography variant="caption" component="p" sx={{ my: 0.5 }}>Context for the next message. Mode and attachments are sent separately.</Typography>
		{entries.length ? <Box component="dl" sx={{ m: 0, maxHeight: 160, overflow: 'auto', fontSize: '0.7rem', overflowWrap: 'anywhere' }}>
			{entries.map(([key, value]) => <Box key={key} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)', gap: 1, py: 0.25 }}>
				<Box component="dt" sx={{ fontWeight: 600 }}>{key}</Box>
				<Box component="dd" sx={{ m: 0, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{value}</Box>
			</Box>)}
		</Box> : <Typography variant="caption">{emptyText}</Typography>}
	</Box>;
}
